"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

interface Slide {
  id: string;
  secureUrl: string;
  alt: string;
  caption?: string;
}

interface SlideshowProps {
  title?: string;
  description?: string;
  slides: Slide[];
  autoplay?: boolean;
  delayMs?: number;
  maxWidth?: "default" | "wide" | "full";
}

const MAX_WIDTH_CLASSES: Record<
  NonNullable<SlideshowProps["maxWidth"]>,
  string
> = {
  default: "max-w-4xl",
  wide: "max-w-6xl",
  full: "max-w-none",
};

const Slideshow = ({
  title,
  description,
  slides,
  autoplay = false,
  delayMs = 5000,
  maxWidth = "default",
}: SlideshowProps) => {
  const hasSlides = slides && slides.length > 0;
  const safeDelay = Math.min(Math.max(delayMs, 1500), 20000);
  const [currentIndex, setCurrentIndex] = useState(0);

  const preparedSlides = useMemo(
    () =>
      slides?.map((slide) => ({
        ...slide,
        alt: slide.alt || "Slideshow image",
      })) || [],
    [slides]
  );

  useEffect(() => {
    if (!autoplay || !hasSlides) return;

    const intervalId = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % preparedSlides.length);
    }, safeDelay);

    return () => window.clearInterval(intervalId);
  }, [autoplay, hasSlides, preparedSlides.length, safeDelay]);

  const handlePrev = () => {
    if (!hasSlides) return;
    setCurrentIndex((prev) =>
      prev === 0 ? preparedSlides.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    if (!hasSlides) return;
    setCurrentIndex((prev) => (prev + 1) % preparedSlides.length);
  };

  if (!hasSlides) return null;

  const activeSlide = preparedSlides[currentIndex];
  const widthClass = MAX_WIDTH_CLASSES[maxWidth] || MAX_WIDTH_CLASSES.default;

  return (
    <section className={`mx-auto w-full ${widthClass}`}>
      <div className="relative overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
        <div className="relative aspect-video w-full bg-gray-50">
          <Image
            src={activeSlide.secureUrl}
            alt={activeSlide.alt}
            fill
            sizes="(min-width: 1024px) 960px, 100vw"
            className="h-full w-full object-cover"
            priority={autoplay}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10" />
          {activeSlide.caption && (
            <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/60 via-black/40 to-transparent px-4 py-3 text-sm text-white">
              {activeSlide.caption}
            </div>
          )}
        </div>

        <div className="absolute inset-y-0 left-0 flex items-center">
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous slide"
            className="ml-2 rounded-full bg-white/80 p-2 text-gray-800 shadow hover:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            ‹
          </button>
        </div>

        <div className="absolute inset-y-0 right-0 flex items-center">
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next slide"
            className="mr-2 rounded-full bg-white/80 p-2 text-gray-800 shadow hover:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            ›
          </button>
        </div>

        <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs text-gray-700 shadow">
          {preparedSlides.map((slide, index) => (
            <button
              key={slide.id || index}
              type="button"
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`size-2 rounded-full ${
                index === currentIndex ? "bg-violet-600" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Slideshow;
